import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import classnames from 'classnames';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Form,
  Loader,
  Button,
  Badge,
  DocusignViewer,
  useModal,
  useWatch,
} from '@lucid/lego';
import type { FormRef } from '@lucid/lego';
import { useStore } from '../../../../../store/store';
import { useUserIdentity } from '@lucid/portal-core';
import {
  useCreateCheckInAgreement,
  useUpdateProspect,
} from '../../../../../api/services/lupp/prospect/prospect';
import type {
  Prospect,
  UpdateProspectRequest,
} from '../../../../../api/services/lupp/prospect/dto.prospect';
import {
  useGetTestDriveAvailableSlots,
  useGetTestDriveVehicles,
} from '../../../../../api/services/lupp/test-drive/test-drive';
import { LUPP_QUERY_KEYS } from '../../../../../api/services/lupp/constants.lupp';
import {
  formatOffsetDateTime,
  buildProspectPayload,
  updateProspectContactTrackings,
  getNextEngagementDate,
  getClosedStatusLog,
  resolveClosedSubStatusLabel,
  resolvePrimaryReasonLabel,
  resolveSecondaryReasonLabel,
} from '../utils';
import {
  localToUTC,
  zonedToUTC,
  utcToZonedTimeSlot,
  toZuluDatetime,
  getContentfulLocaleFromLanguage,
  convertToContentfulLocale,
} from '../../../../../utils/tools';
import { createLeadFormFields, createTestDriveFormFields } from './formFields';
import {
  isTestDriveCheckInEnabled,
  isTestDriveCheckInAlwaysVisible,
} from '../../../../../config/featureFlags';
import ChangeLocationField from './ChangeLocationField';
import styles from './DetailsTab.module.scss';

/** Delay before hiding success message after form submission (ms) */
const SUCCESS_MESSAGE_DELAY = 3000;
/** Delay to allow form initialization before setting values (ms) */
const FORM_INIT_DELAY = 300;
const CHECK_IN_DOCUMENT_LOCALES = [
  'en_us',
  'en_ca',
  'fr_ca',
  'fr_ch',
  'de_ch',
  'no_no',
  'nl_nl',
  'de_de',
  'ar_sa',
  'en_nl',
  'en_de',
  'en_no',
  'en_fr',
  'en_ch',
];
const DOCUMENT_LOCALE_OPTIONS = CHECK_IN_DOCUMENT_LOCALES.map((documentLocale) => ({
  label: documentLocale.replace('_', '-'),
  value: documentLocale,
}));

type ProspectDetailProps = {
  prospect?: Prospect;
  onDirtyChange?: (isDirty: boolean) => void;
  onCancel?: () => void;
  isTestDriveTab?: boolean;
  labels: Record<string, string>;
};

const getDocumentLocale = (value?: string) => {
  const normalized = value?.toLowerCase().replace('-', '_');
  return normalized && CHECK_IN_DOCUMENT_LOCALES.includes(normalized) ? normalized : undefined;
};

const getProspectDocumentLocale = (prospect?: Prospect) => {
  const countryCode = prospect?.countryCode?.toLowerCase();
  const language = prospect?.preferredLanguageIso?.toLowerCase();
  const candidate = countryCode && language ? `${language}_${countryCode}` : undefined;
  return (
    getDocumentLocale(candidate) ?? getDocumentLocale(countryCode ? `en_${countryCode}` : undefined)
  );
};

const DetailsTab: React.FC<ProspectDetailProps> = ({
  prospect,
  onDirtyChange,
  onCancel,
  isTestDriveTab = false,
  labels,
}) => {
  const { userIdentity } = useUserIdentity();
  const theme = useStore((state) => state.theme);
  const locale = useStore((state) => state.locale);
  const language = useStore((state) => state.language);
  // Date format follows language preference; falls back to store locale if no language override
  const languageBasedLocale = useMemo(
    () =>
      language ? getContentfulLocaleFromLanguage(language) : convertToContentfulLocale(locale),
    [language, locale]
  );
  const queryClient = useQueryClient();
  const { mutate, isPending } = useUpdateProspect();
  const { mutateAsync: createCheckInAgreement, isPending: isCreatingCheckInAgreement } =
    useCreateCheckInAgreement();

  // Scopes vehicles and slots to the prospect's location rather than the whole agency.
  const locationId = prospect?.businessPartnerOrganizationLocationIdentifier;

  const { data: testDriveVehicles } = useGetTestDriveVehicles({
    agencyId: userIdentity?.businessOrganizationId ?? '',
    locationId,
    enabled: isTestDriveTab,
  });

  const docusignModal = useModal();
  const invertedTheme = theme === 'dark' ? 'light' : 'dark';
  const isClosedProspect = prospect?.statusName?.toLowerCase() === 'closed';
  const closedStatusLog = useMemo(() => getClosedStatusLog(prospect), [prospect]);
  const closedSubStatusLabel = useMemo(
    () => resolveClosedSubStatusLabel(closedStatusLog?.subStatusName, labels),
    [closedStatusLog, labels]
  );
  const primaryReasonLabel = useMemo(
    () => resolvePrimaryReasonLabel(closedStatusLog?.primaryReasonName, labels),
    [closedStatusLog, labels]
  );
  const secondaryReasonLabel = useMemo(
    () =>
      resolveSecondaryReasonLabel(
        closedStatusLog?.primaryReasonName,
        closedStatusLog?.secondaryReasonName,
        labels
      ),
    [closedStatusLog, labels]
  );
  const notAvailableLabel = labels?.['details.notAvailable'] ?? 'Not available';
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [docusignUrl, setDocusignUrl] = useState<string | null>(null);
  const [selectedCheckInLocale, setSelectedCheckInLocale] = useState<string | undefined>();
  const [isLocaleMenuOpen, setIsLocaleMenuOpen] = useState(false);
  const localeMenuRef = useRef<HTMLDivElement>(null);
  const isInitialMount = useRef(true);
  const messageRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<FormRef>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (localeMenuRef.current && !localeMenuRef.current.contains(event.target as Node)) {
        setIsLocaleMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const selectedModelTrim = useWatch<string>(formRef, 'selectedModelTrim');
  const scheduledDate = useWatch<string>(formRef, 'scheduledDate');
  const isTestDriveOn = Boolean(useWatch<string[]>(formRef, 'testDriveNow')?.includes('yes'));

  // The dropdown holds the vehicle's description, but slots are keyed by model + trim.
  const selectedVehicle = testDriveVehicles?.find(
    (vehicle) => vehicle.description === selectedModelTrim
  );

  const { data: availableSlots } = useGetTestDriveAvailableSlots({
    agencyId: userIdentity?.businessOrganizationId ?? '',
    locationId,
    model: selectedVehicle?.model ?? '',
    trim: selectedVehicle?.trim,
    selectedDate: scheduledDate ?? '',
    enabled: isTestDriveTab,
  });

  // Slot times are wall-clock in the agency's timezone, which the slots carry.
  const agencyTimeZone = availableSlots?.slotDetails?.[0]?.timezone;

  // A slot only exists for one model/date pair, so drop it when either changes.
  // Starts null so the saved schedule's time survives the initial render.
  const slotContextRef = useRef<string | null>(null);
  useEffect(() => {
    const slotContext = `${selectedModelTrim ?? ''}|${scheduledDate ?? ''}`;
    if (slotContextRef.current !== null && slotContextRef.current !== slotContext) {
      // shouldValidate defaults to true, which would flag "required" before the user picks.
      formRef.current?.setValue('scheduledTime', '', { shouldValidate: false });
    }
    slotContextRef.current = slotContext;
  }, [selectedModelTrim, scheduledDate]);

  // Use refs to keep stable references and prevent form reset
  const onCancelRef = useRef(onCancel);
  const onDirtyChangeRef = useRef(onDirtyChange);

  const organizationName = userIdentity?.organizationName;
  const partnerOrgLocation = userIdentity?.businessPartnerOrgLocation;

  // Update refs when props change
  useEffect(() => {
    onCancelRef.current = onCancel;
    onDirtyChangeRef.current = onDirtyChange;
  }, [onCancel, onDirtyChange]);

  // Reset initial mount flag when prospect or tab changes
  useEffect(() => {
    isInitialMount.current = true;
    // Notify parent that form is clean on mount/tab change
    onDirtyChangeRef.current?.(false);
    // Delay to allow form to fully initialize before accepting dirty state changes
    // The Form component's watch() triggers immediately, so we need to wait longer
    const timer = setTimeout(() => {
      isInitialMount.current = false;
    }, FORM_INIT_DELAY);
    return () => clearTimeout(timer);
  }, [prospect?.prospectIdentifier, isTestDriveTab]);

  // Update nextEngagementDate field when prospect data changes
  useEffect(() => {
    if (formRef.current && prospect) {
      const nextEngagementDate = getNextEngagementDate(prospect);
      if (nextEngagementDate) {
        formRef.current.setValue('nextEngagementDate', nextEngagementDate);
      }
    }
  }, [prospect]);

  const handleFormDirtyChange = useCallback((isDirty: boolean) => {
    // Ignore dirty state changes during initial mount to prevent false positives
    if (isInitialMount.current) {
      return;
    }
    onDirtyChangeRef.current?.(isDirty);
  }, []);

  // Stable cancel handler that doesn't change reference
  const handleCancel = useCallback(() => {
    onCancelRef.current?.();
  }, []);

  // Get the first active test drive schedule for the form
  const activeTestDriveSchedule = useMemo(() => {
    const schedules = prospect?.testDriveSchedules || [];
    return schedules.find((schedule) => schedule.isActive && !schedule.isDeleted) || schedules[0];
  }, [prospect?.testDriveSchedules]);

  // Maps the saved model/trim onto a vehicle, falling back to any vehicle of the
  // same model so the rep still sees whether it was an Air or a Gravity.
  const savedVehicle = useMemo(
    () =>
      testDriveVehicles?.find(
        (vehicle) =>
          vehicle.model === activeTestDriveSchedule?.model &&
          vehicle.trim === activeTestDriveSchedule?.trim
      ) ?? testDriveVehicles?.find((vehicle) => vehicle.model === activeTestDriveSchedule?.model),
    [testDriveVehicles, activeTestDriveSchedule]
  );

  // The form captures its defaults on init, before the vehicles and the agency
  // timezone have loaded, so the saved selection has to be applied afterwards.
  const hasRestoredSlotRef = useRef(false);
  useEffect(() => {
    if (hasRestoredSlotRef.current || !savedVehicle) return;

    // On a cold page load the form mounts before testDriveVehicles has resolved, so its
    // defaultValue falls back to the raw saved trim/model (e.g. "Touring") instead of the
    // full vehicle description the dropdown options use (e.g. "Lucid Air Touring"). That
    // stale placeholder is truthy, so also correct it once the real vehicle list arrives.
    const isValidSelection = testDriveVehicles?.some(
      (vehicle) => vehicle.description === selectedModelTrim
    );
    if (!selectedModelTrim || !isValidSelection) {
      formRef.current?.setValue('selectedModelTrim', savedVehicle.description, {
        shouldValidate: false,
        shouldDirty: false,
      });
    }

    if (!agencyTimeZone) return;
    const savedSlot = utcToZonedTimeSlot(
      activeTestDriveSchedule?.scheduledStartDatetime,
      activeTestDriveSchedule?.scheduledEndDatetime,
      agencyTimeZone
    );
    if (!savedSlot) return;
    hasRestoredSlotRef.current = true;
    formRef.current?.setValue('scheduledTime', savedSlot, {
      shouldValidate: false,
      shouldDirty: false,
    });
  }, [savedVehicle, selectedModelTrim, agencyTimeZone, activeTestDriveSchedule, testDriveVehicles]);

  const resolvedCheckInLocale = useMemo(
    () =>
      getDocumentLocale(selectedCheckInLocale) ??
      getDocumentLocale(locale) ??
      getDocumentLocale(window.location.pathname.split('/')[1]) ??
      getProspectDocumentLocale(prospect),
    [locale, prospect, selectedCheckInLocale]
  );

  const canCheckIn = useMemo(() => {
    if (
      !isTestDriveCheckInEnabled() ||
      !isTestDriveTab ||
      !prospect?.prospectIdentifier ||
      !activeTestDriveSchedule?.testDriveScheduleId
    ) {
      return false;
    }
    if (isTestDriveCheckInAlwaysVisible()) {
      return true;
    }
    if (
      !activeTestDriveSchedule.sfdcSalesEventId ||
      !activeTestDriveSchedule.scheduledStartDatetime
    ) {
      return false;
    }
    try {
      const timezone = activeTestDriveSchedule.timezone || 'UTC';
      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      return (
        formatter.format(new Date(activeTestDriveSchedule.scheduledStartDatetime)) ===
        formatter.format(new Date())
      );
    } catch {
      return false;
    }
  }, [activeTestDriveSchedule, isTestDriveTab, prospect?.prospectIdentifier]);

  const handleCheckIn = useCallback(async () => {
    if (
      !prospect?.prospectIdentifier ||
      !activeTestDriveSchedule?.testDriveScheduleId ||
      !resolvedCheckInLocale
    ) {
      return;
    }
    setErrorMessage(null);
    setIsLocaleMenuOpen(false);
    try {
      const response = await createCheckInAgreement({
        prospectId: prospect.prospectIdentifier,
        testDriveId: activeTestDriveSchedule.testDriveScheduleId,
        data: { preferredLocale: resolvedCheckInLocale },
      });
      if (!response.viewUrl) {
        throw new Error('Missing DocuSign signing URL');
      }
      setDocusignUrl(response.viewUrl);
      docusignModal.openModal();
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : (labels?.['details.checkInError'] ?? 'Unable to generate the Check-In agreement.')
      );
    }
  }, [
    prospect?.prospectIdentifier,
    activeTestDriveSchedule?.testDriveScheduleId,
    resolvedCheckInLocale,
    createCheckInAgreement,
    docusignModal,
    labels,
  ]);

  const handleCloseDocusign = () => {
    docusignModal.closeModal();
    setDocusignUrl(null);
  };

  const formFields = useMemo(
    () =>
      isTestDriveTab
        ? createTestDriveFormFields(
            prospect,
            handleCancel,
            activeTestDriveSchedule,
            organizationName,
            labels,
            languageBasedLocale,
            partnerOrgLocation,
            testDriveVehicles,
            availableSlots?.slotDetails,
            { modelTrim: selectedModelTrim, date: scheduledDate },
            agencyTimeZone,
            isTestDriveOn
          )
        : createLeadFormFields(
            prospect,
            handleCancel,
            organizationName,
            labels,
            languageBasedLocale
          ),
    [
      prospect,
      handleCancel,
      isTestDriveTab,
      activeTestDriveSchedule,
      organizationName,
      labels,
      languageBasedLocale,
      partnerOrgLocation,
      testDriveVehicles,
      availableSlots,
      selectedModelTrim,
      scheduledDate,
      agencyTimeZone,
      isTestDriveOn,
    ]
  );

  // Cleanup timeout on unmount to prevent memory leaks
  // Clear success message after delay (slider stays open)
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    if (successMessage) {
      timeoutId = setTimeout(() => {
        setSuccessMessage('');
      }, SUCCESS_MESSAGE_DELAY);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [successMessage]);

  // Scroll to message when error or success message appears
  useEffect(() => {
    if ((errorMessage || successMessage) && messageRef.current) {
      // Find the scrollable parent (slider body with overflow-y: auto)
      let parent = messageRef.current.parentElement;
      while (parent) {
        const style = window.getComputedStyle(parent);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
          parent.scrollTo({ top: 0, behavior: 'smooth' });
          break;
        }
        parent = parent.parentElement;
      }
    }
  }, [errorMessage, successMessage]);

  const handleSuccess = (data: unknown) => {
    if (!prospect?.prospectIdentifier) {
      setErrorMessage(
        labels?.['details.prospectIdMissing'] ?? 'Prospect ID is missing. Cannot update.'
      );
      return;
    }

    // Prevent updates when status is Closed
    if (prospect?.statusName?.toLowerCase() === 'closed') {
      setErrorMessage(
        labels?.['details.closedProspectError'] ??
          'Cannot update a closed prospect. Please reopen the prospect to make changes.'
      );
      setSuccessMessage('');
      return;
    }

    setErrorMessage(null);

    // Get current timestamp for all date fields
    const currentDate = new Date();
    const now = formatOffsetDateTime(currentDate);

    let payload: UpdateProspectRequest;

    if (isTestDriveTab) {
      // Handle Test Drive form data
      const testDriveFormData = data as {
        testDriveNow?: string[];
        scheduledDate?: string;
        scheduledTime?: string;
        status?: string;
        selectedModelTrim?: string;
      };

      // Parse time slot to get start and end times
      const timeSlot = testDriveFormData.scheduledTime || '';
      const [startTime, endTime] = timeSlot.split('-').map((t) => t?.trim() || '');

      // Build scheduled datetime from date and time
      const scheduledDate = testDriveFormData.scheduledDate || '';
      const buildDatetime = (date: string, time: string): string => {
        if (!date || !time) return now;
        try {
          // Convert date to YYYY-MM-DD format if needed
          let isoDate = date;
          if (date.includes('/')) {
            // Date is in MM/DD/YYYY format, convert to YYYY-MM-DD
            const parts = date.split('/');
            if (parts.length === 3) {
              const [month, day, year] = parts;
              isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            }
          }

          const [hours, minutes] = time.split(':').map(Number);
          // Slots are offered in the agency's timezone, so convert against that
          // rather than the viewing user's locale.
          return agencyTimeZone
            ? zonedToUTC(isoDate, hours, minutes, agencyTimeZone)
            : localToUTC(isoDate, hours, minutes, locale);
        } catch {
          return now;
        }
      };

      // Determine if test drive is selected
      const isTestDrive = testDriveFormData.testDriveNow?.includes('yes') ?? false;

      // The chosen slot names the specific car, which is the only place eventConfigId
      // and vin exist. Falls back to the saved record when the slot is no longer offered.
      const selectedSlot = availableSlots?.slotDetails?.find(
        (slot) =>
          utcToZonedTimeSlot(slot.startTime, slot.endTime, slot.timezone) ===
          testDriveFormData.scheduledTime
      );
      const slotVehicle =
        selectedSlot?.tdEventConfig?.find(
          (config) =>
            config.model === selectedVehicle?.model && config.trim === selectedVehicle?.trim
        ) ?? selectedSlot?.tdEventConfig?.[0];

      // Update or create test drive schedule
      const existingSchedule = activeTestDriveSchedule;
      const updatedTestDriveSchedule = {
        ...existingSchedule,
        testDriveStatus: testDriveFormData.status || existingSchedule?.testDriveStatus,
        scheduledStartDatetime: toZuluDatetime(buildDatetime(scheduledDate, startTime)),
        scheduledEndDatetime: toZuluDatetime(buildDatetime(scheduledDate, endTime)),
        appointmentLocationName:
          existingSchedule?.appointmentLocationName ||
          partnerOrgLocation ||
          prospect.prospectOwnerLocation ||
          '',
        eventConfigId: slotVehicle?.eventConfigId ?? existingSchedule?.eventConfigId,
        vin: slotVehicle?.vin ?? existingSchedule?.vin,
        // A test drive booked from here is hosted by whoever is signed in.
        businessPartnerUserId:
          existingSchedule?.businessPartnerUserId ?? userIdentity?.userPortalId,
        businessPartnerOrganizationId:
          existingSchedule?.businessPartnerOrganizationId ?? userIdentity?.businessOrganizationId,
        make: slotVehicle?.make ?? selectedVehicle?.make ?? existingSchedule?.make,
        trim:
          selectedVehicle?.trim ?? testDriveFormData.selectedModelTrim ?? existingSchedule?.trim,
        model: selectedVehicle?.model ?? existingSchedule?.model,
        timezone: selectedSlot?.timezone ?? agencyTimeZone ?? existingSchedule?.timezone ?? 'GMT',
        isActive: isTestDrive,
        isDeleted: false,
        createdDatetime: existingSchedule?.createdDatetime || toZuluDatetime(now),
        updatedDatetime: toZuluDatetime(now),
        updatedBy: 'portal-user',
      };

      payload = buildProspectPayload(prospect, now, {
        testDriveSchedules: [updatedTestDriveSchedule],
        isTestDrive: isTestDrive,
      });
    } else {
      const { isEmailOptIn, callConsent, ...restFormData } = data as Record<string, unknown>;

      payload = buildProspectPayload(prospect, now, {
        ...restFormData,
        isEmailOptIn: (isEmailOptIn as string[])?.includes('emailOptIn') || null,
        isCallOptIn: callConsent === 'callOptIn' || null,
        isCallOptOut: callConsent === 'callOptOut' || null,
        interestedTrim: Array.isArray(restFormData.interestedTrim)
          ? (restFormData.interestedTrim as string[]).join(',')
          : (restFormData.interestedTrim as string) || prospect.interestedTrim,
        prospectContactTrackings: updateProspectContactTrackings(
          prospect,
          restFormData.nextEngagementDate as string | undefined,
          now
        ),
        isTestDrive: activeTestDriveSchedule?.isActive ?? false,
      } as Partial<Prospect>);
    }

    // Remove email from payload to prevent updates (applies to both test drive and details)
    const { prospectEmail: _prospectEmail, ...payloadWithoutEmail } = payload;
    payload = payloadWithoutEmail as Prospect;

    mutate(
      { id: prospect.prospectIdentifier, data: payload },
      {
        onSuccess: () => {
          // Booking a slot changes what is still bookable, and slots sit under their
          // own query key, so the prospect invalidation does not cover them.
          if (isTestDriveTab) {
            queryClient.invalidateQueries({
              queryKey: [LUPP_QUERY_KEYS.TEST_DRIVE_AVAILABLE_SLOTS],
            });
          }
          // Reset dirty state to prevent unsaved changes modal
          onDirtyChangeRef.current?.(false);
          setSuccessMessage(labels?.['details.updateSuccess'] ?? 'Prospect updated successfully!');
        },
        onError: (error: unknown) => {
          setSuccessMessage('');
          const errorMsg =
            error instanceof Error
              ? error.message
              : (labels?.['details.error'] ?? 'Failed to update prospect. Please try again.');
          setErrorMessage(errorMsg);
        },
      }
    );
  };

  const handleError = (error: unknown) => {
    console.error('Form validation failed:', error);
    setErrorMessage(
      labels?.['details.validationError'] ?? 'Please fix the form errors before submitting.'
    );
    setSuccessMessage('');
  };

  const CheckInActionControl: React.FC = useCallback(() => {
    if (!canCheckIn) {
      return null;
    }

    return (
      <div
        ref={localeMenuRef}
        className={classnames(styles.checkInActionContainer, {
          [styles.disabled]: isCreatingCheckInAgreement,
        })}
      >
        <Button
          className={styles.checkInMainButton}
          dataTestId="check-in-button"
          gaComponent="prospect-test-drive-check-in"
          handleClick={() => handleCheckIn()}
          disabled={isCreatingCheckInAgreement || !resolvedCheckInLocale}
          type="primary"
          color="neutral"
          theme={theme}
          isFluid={true}
          size="large"
          label={
            resolvedCheckInLocale
              ? `${labels?.['details.checkIn'] ?? 'Check-In'} (${DOCUMENT_LOCALE_OPTIONS.find((opt) => opt.value === resolvedCheckInLocale)?.label ?? resolvedCheckInLocale})`
              : (labels?.['details.checkIn'] ?? 'Check-In')
          }
        />
        <Button
          className={styles.checkInDropdownToggle}
          dataTestId="check-in-locale-selector"
          gaComponent="prospect-test-drive-check-in-locale-menu"
          handleClick={() => setIsLocaleMenuOpen((prev) => !prev)}
          disabled={isCreatingCheckInAgreement}
          type="primary"
          color="neutral"
          theme={theme}
          size="large"
          iconRight={{
            category: 'arrows',
            name: isLocaleMenuOpen ? 'chevron-up' : 'chevron-down',
            dataTestId: 'check-in-locale-chevron',
          }}
        />
        {isLocaleMenuOpen && (
          <ul className={styles.checkInMenu} data-testid="check-in-locale-menu">
            <li className={styles.checkInMenuHeader}>
              {labels?.['details.checkInDocumentLocale'] ?? 'Document language'}
            </li>
            {DOCUMENT_LOCALE_OPTIONS.map((option) => {
              const isSelected = option.value === resolvedCheckInLocale;
              return (
                <li
                  key={option.value}
                  className={classnames(styles.checkInMenuItem, {
                    [styles.selected]: isSelected,
                  })}
                  data-testid={`check-in-locale-option-${option.value}`}
                  onClick={() => {
                    setSelectedCheckInLocale(option.value);
                    setIsLocaleMenuOpen(false);
                  }}
                >
                  <span>{option.label}</span>
                  {isSelected && <span>✓</span>}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  }, [
    canCheckIn,
    isCreatingCheckInAgreement,
    resolvedCheckInLocale,
    theme,
    labels,
    isLocaleMenuOpen,
    handleCheckIn,
  ]);

  // Location changes save outside the form, so route their results to the page banner.
  const ProspectChangeLocationField = useCallback(
    (props: React.ComponentProps<typeof ChangeLocationField>) => (
      <ChangeLocationField
        {...props}
        onUpdateSuccess={(message) => {
          setErrorMessage(null);
          setSuccessMessage(message);
        }}
        onUpdateError={(message) => {
          setSuccessMessage('');
          setErrorMessage(message);
        }}
      />
    ),
    []
  );

  const customComponents = useMemo(
    () => ({
      CheckInActionControl,
      ChangeLocationField: ProspectChangeLocationField as React.ComponentType<
        Record<string, unknown>
      >,
    }),
    [CheckInActionControl, ProspectChangeLocationField]
  );

  return (
    <Box ref={messageRef}>
      {successMessage && (
        <Box mb={16} pt={12} pb={12} pl={16} pr={16} className={styles.successMessageText}>
          <Typography variant="body" size="medium" color="white" theme={invertedTheme}>
            {successMessage}
          </Typography>
        </Box>
      )}
      {errorMessage && (
        <Box mb={16} pt={12} pb={12} pl={16} pr={16} className={styles.errorMessageText}>
          <Typography variant="body" size="medium" color="white" theme={invertedTheme}>
            {errorMessage}
          </Typography>
        </Box>
      )}
      {isPending && (
        <Box className="fetchingOverlay">
          <Loader size={32} theme={invertedTheme} />
        </Box>
      )}
      {!isTestDriveTab && (
        <Box mb={16} pt={12} pb={12} pl={16} pr={16} className={styles.prospectFlagsBanner}>
          <Box>
            <Typography variant="label" size="small" theme={invertedTheme} color="warning-1">
              {labels?.['details.existingLucidCustomer'] ?? 'Existing Lucid Customer'}
            </Typography>
            <Typography variant="body" size="medium" theme={invertedTheme} color="success-1">
              {prospect?.isExistingLucidCustomer == null
                ? '-'
                : prospect.isExistingLucidCustomer
                  ? 'Yes'
                  : 'No'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="label" size="small" theme={invertedTheme} color="warning-1">
              {labels?.['details.openProspectElsewhereInNetwork'] ??
                'Open Prospect Elsewhere in Network'}
            </Typography>
            <Typography variant="body" size="medium" theme={invertedTheme} color="success-1">
              {prospect?.isOpenProspectElsewhereInNetwork == null
                ? '-'
                : prospect.isOpenProspectElsewhereInNetwork
                  ? 'Yes'
                  : 'No'}
            </Typography>
          </Box>
        </Box>
      )}
      {!isTestDriveTab && isClosedProspect && (
        <Box mb={16} pt={12} pb={12} pl={16} pr={16} className={styles.prospectClosureBanner}>
          <Typography variant="label" size="medium" theme={invertedTheme}>
            {labels?.['details.prospectClosure'] ?? 'Prospect Closure'}
          </Typography>
          <Box mt={12} className={styles.prospectClosureReasonRow}>
            <Box>
              <Typography variant="label" size="small" theme={invertedTheme} color="gray-3">
                {labels?.['details.closedSubStatus'] ?? 'Closed Sub-status'}
              </Typography>
              <Typography variant="body" size="medium" theme={invertedTheme}>
                {closedSubStatusLabel}
              </Typography>
            </Box>
            <Box className={styles.prospectClosureReasonGroup}>
              <Box>
                <Typography variant="label" size="small" theme={invertedTheme} color="gray-3">
                  {labels?.['details.primaryReason'] ?? 'Primary Reason'}
                </Typography>
                <Badge
                  label={primaryReasonLabel}
                  color={primaryReasonLabel === notAvailableLabel ? 'red' : 'blue'}
                  type="secondary"
                  theme={invertedTheme}
                />
              </Box>
              <Box>
                <Typography variant="label" size="small" theme={invertedTheme} color="gray-3">
                  {labels?.['details.secondaryReason'] ?? 'Secondary Reason'}
                </Typography>
                <Badge
                  label={secondaryReasonLabel}
                  color={secondaryReasonLabel === notAvailableLabel ? 'red' : 'blue'}
                  type="secondary"
                  theme={invertedTheme}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      <Form
        ref={formRef}
        id="create-prospect-form"
        theme={invertedTheme}
        formFields={formFields}
        customComponents={customComponents}
        onSuccess={handleSuccess}
        onError={handleError}
        onDirtyChange={handleFormDirtyChange}
        disableSubmitUntilDirty
        isFluid={true}
        locale={languageBasedLocale}
        language={language ?? undefined}
      />
      {docusignUrl && (
        <DocusignViewer
          url={docusignUrl}
          dataTestId="check-in-docusign-viewer"
          isOpen={docusignModal.isOpen}
          onClose={handleCloseDocusign}
          theme={invertedTheme}
          labels={{
            title: labels?.['details.checkIn'] ?? 'Check-In',
            loading: labels?.['details.checkInLoading'] ?? 'Loading document...',
            error: labels?.['details.checkInViewerError'] ?? 'Failed to load the document.',
          }}
        />
      )}
    </Box>
  );
};

export default DetailsTab;
